import { APP_TABLE_PHONE_QUERY } from "components/new-stuff/app-table"
import EnhancedReportTable from "components/new-stuff/report-table/EnhancedReportTable"
import useMediaQuery from "hooks/useMediaQuery"
import { formatReportDate } from "../../utils/report-date"
import type { ExportFooterCell } from "components/new-stuff/report-table/types"
import { Table } from "antd"
import type { ReactNode } from "react"
import { useMemo } from "react"
import { formatCurrencyNoSymbol } from "utils/currency"

type Props = {
  data: any
  rows: any[]
  rowType: "Employee" | "Department" | "Workplace"
  title?: string
  user: any
  variant?: "compact" | "detailed"
}

const EARNING_TYPES = [1, 3, 4, 7, 8]
const LEGACY_PAYROLL_EARNING_TYPES = [1, 3, 4, 7, 8]
const GROSS_EARNING_TYPES = [1, 4, 8]
const OTHER_PAYABLE_TYPES = [3, 7]
const REIMBURSEMENT_TYPE = 7

enum TaxColumns {
  CONSOLIDATED_TAX = "Consolidated Salary Tax",
  TOTAL_ALLOWANCE_TAX = "Total Allowance Tax",
  TAX_RETRO = "Tax Retro",
  TAX_DIFFERENTIAL = "Tax Differential",
  TAX = "Tax",
}

export const PayrollJournalEnhancedTable = ({
  data,
  rows,
  rowType,
  title,
  user,
  variant = "compact",
}: Props) => {
  const allItems = useMemo(
    () => rows.flatMap((row) => row?.groups || []),
    [rows]
  )

  const allEarningDescriptions = useMemo(
    () => getDescriptions(allItems, (item) => isEarningItem(item)),
    [allItems]
  )

  const isOandoConsolidatedReport = useMemo(
    () =>
      String(user?.userOrganisationId) === "354" &&
      allEarningDescriptions.some(isConsolidatedSalary),
    [user, allEarningDescriptions]
  )

  const earningDescriptions = useMemo(() => {
    if (!isOandoConsolidatedReport) {
      return sortConsolidatedSalaryFirst(allEarningDescriptions)
    }
    const regular = sortDescriptionsWithRetrosNextToBase(
      getDescriptions(allItems, (item) => isRegularEarningItem(item))
    )
    const reimbursements = sortDescriptionsWithRetrosNextToBase(
      getDescriptions(allItems, isReimbursementItem)
    )
    return [...regular, ...reimbursements]
  }, [allItems, allEarningDescriptions, isOandoConsolidatedReport])

  const grossEarningDescriptions = useMemo(
    () =>
      sortConsolidatedSalaryFirst(
        getDescriptions(allItems, isGrossEarningItem)
      ),
    [allItems]
  )

  const otherPayableDescriptions = useMemo(
    () =>
      getDescriptions(allItems, isOtherPayableItem).sort((a, b) =>
        a.localeCompare(b)
      ),
    [allItems]
  )

  const deductionDescriptions = useMemo(() => {
    const descriptions = isOandoConsolidatedReport
      ? getDeductionDescriptions(allItems)
      : getDescriptions(allItems, isDeductionItem, getDeductionDescription)

    return isOandoConsolidatedReport
      ? sortDeductionDescriptions(descriptions)
      : sortLegacyDeductionDescriptions(descriptions)
  }, [allItems, isOandoConsolidatedReport])

  const oandoTaxDescriptions = useMemo(
    () => getOandoTaxDescriptions(allItems, isOandoConsolidatedReport),
    [allItems, isOandoConsolidatedReport]
  )

  const combinedDeductionDescriptions = useMemo(() => {
    if (!isOandoConsolidatedReport) {
      return [...deductionDescriptions, ...oandoTaxDescriptions]
    }

    const firstTaxIdx = deductionDescriptions.findIndex(isTaxDescription)
    const [nonTax, tax] =
      firstTaxIdx === -1
        ? [deductionDescriptions, [] as string[]]
        : [
            deductionDescriptions.slice(0, firstTaxIdx),
            deductionDescriptions.slice(firstTaxIdx),
          ]

    return [
      ...nonTax,
      TaxColumns.CONSOLIDATED_TAX,
      ...tax,
      ...oandoTaxDescriptions,
    ]
  }, [isOandoConsolidatedReport, deductionDescriptions, oandoTaxDescriptions])

  const contributionDescriptions = useMemo(
    () =>
      isOandoConsolidatedReport
        ? sortContributionDescriptions(
            getDescriptions(allItems, isContributionItem)
          )
        : getDescriptions(allItems, isContributionItem),
    [allItems, isOandoConsolidatedReport]
  )

  const amortizationDescriptions = useMemo(
    () =>
      getDescriptions(allItems, isAmortizationItem).sort((a, b) =>
        a.localeCompare(b)
      ),
    [allItems]
  )

  const identityLeafCount = useMemo(
    () =>
      (variant === "detailed" && rowType === "Employee"
        ? buildDetailedIdentityColumns(rowType).length
        : 1) + (shouldShowDaysWorkedColumn(data, rowType) ? 1 : 0),
    [data, variant, rowType]
  )

  const tableData = useMemo(
    () =>
      rows.map((row, index) => ({
        key: `${rowType}-${getRowId(row, rowType) || index}`,
        label: getRowLabel(row, rowType),
        meta: getRowMeta(row, rowType),
        identity: getRowIdentity(row, rowType),
        daysWorked: getDaysWorkedLabel(row),
        groups: row?.groups || [],
        totals: getRowTotals(row?.groups || [], data),
      })),
    [data, rowType, rows]
  )

  const columns = useMemo(() => {
    const generatedColumns: any[] =
      variant === "detailed" && rowType === "Employee"
        ? [buildDetailedIdentityGroup(rowType)]
        : [buildCompactIdentityColumn(rowType)]

    if (shouldShowDaysWorkedColumn(data, rowType)) {
      generatedColumns.push(buildDaysWorkedColumn())
    }

    if (data?.employeeEarnings) {
      if (isOandoConsolidatedReport) {
        generatedColumns.push(
          buildOandoEarningsGroup(earningDescriptions, user)
        )
      } else {
        if (grossEarningDescriptions.length) {
          generatedColumns.push(
            buildAmountGroup(
              "Gross Earnings",
              "gross_earnings",
              grossEarningDescriptions,
              "earningValues",
              user
            )
          )
        }

        if (otherPayableDescriptions.length) {
          generatedColumns.push(
            buildAmountGroup(
              "Other Payables",
              "other_payables",
              otherPayableDescriptions,
              "earningValues",
              user
            )
          )
        }
      }
    } else {
      generatedColumns.push({
        title: "Gross Earnings",
        key: "total_earnings",
        render: (_value: any, record: any) =>
          money(user, record.totals.totalEarnings, record.totals.currency),
      })
    }

    const deductionsColumnGroup = (() => {
      if (!data?.deductions) {
        return [
          {
            title: "Total deductions",
            key: "total_deductions",
            render: (_value: any, record: any) =>
              money(
                user,
                record.totals.totalDeductions,
                record.totals.currency
              ),
          },
          {
            title: "Total tax",
            key: "total_tax",
            render: (_value: any, record: any) =>
              money(user, record.totals.totalTax, record.totals.currency),
          },
        ]
      }

      const deductionsGroup = isOandoConsolidatedReport
        ? buildOandoDeductionsGroup(combinedDeductionDescriptions, user)
        : buildEmployeeDeductionsGroup(
            combinedDeductionDescriptions,
            amortizationDescriptions,
            data,
            user
          )

      if (!isOandoConsolidatedReport) {
        return [deductionsGroup]
      }

      return [deductionsGroup]
    })()

    if (!isOandoConsolidatedReport) {
      generatedColumns.push(...deductionsColumnGroup)
    }

    if (data?.contributions) {
      generatedColumns.push(
        buildAmountGroup(
          "Employer Contributions",
          "contributions",
          contributionDescriptions,
          "contributionValues",
          user,
          "totalContributions"
        )
      )
    }

    if (isOandoConsolidatedReport && amortizationDescriptions.length) {
      generatedColumns.push(
        buildAmountGroup(
          "Amortizations",
          "amortizations",
          amortizationDescriptions,
          "amortizationValues",
          user,
          "totalAmortizations"
        )
      )
    }

    if (isOandoConsolidatedReport) {
      generatedColumns.push(...deductionsColumnGroup)
    }

    generatedColumns.push({
      title: isOandoConsolidatedReport ? "Totals" : "Summary",
      key: isOandoConsolidatedReport ? "totals" : "summary",
      children: [
        {
          title: isOandoConsolidatedReport ? "Net pay" : "Net Salary",
          key: "net_pay",
          render: (_value: any, record: any) =>
            money(user, record.totals.netPay, record.totals.currency),
        },
        ...(isOandoConsolidatedReport && data?.totals
          ? [
              {
                title: "Employer cost",
                key: "employer_cost",
                render: (_value: any, record: any) =>
                  money(
                    user,
                    record.totals.employerCost,
                    record.totals.currency
                  ),
              },
            ]
          : []),
        ...(!isOandoConsolidatedReport
          ? [
              {
                title: "Gross Pay",
                key: "gross_pay",
                render: (_value: any, record: any) =>
                  money(
                    user,
                    record.totals.totalEarningsLegacy,
                    record.totals.currency
                  ),
              },
              {
                title: "Total Deductions",
                key: "summary_total_deductions",
                render: (_value: any, record: any) =>
                  money(
                    user,
                    getTotalEmployeeDeductions(record.totals),
                    record.totals.currency
                  ),
              },
            ]
          : []),
      ],
    })

    return generatedColumns
  }, [
    data,
    rowType,
    user,
    variant,
    earningDescriptions,
    grossEarningDescriptions,
    otherPayableDescriptions,
    combinedDeductionDescriptions,
    contributionDescriptions,
    amortizationDescriptions,
    isOandoConsolidatedReport,
  ])

  const summaryMatrix = useMemo(
    () =>
      buildSummaryMatrix({
        allItems,
        user,
        data,
        identityLeafCount,
        earningDescriptions,
        grossEarningDescriptions,
        otherPayableDescriptions,
        combinedDeductionDescriptions,
        contributionDescriptions,
        amortizationDescriptions,
        isOandoConsolidatedReport,
      }),
    [
      allItems,
      user,
      data,
      identityLeafCount,
      earningDescriptions,
      grossEarningDescriptions,
      otherPayableDescriptions,
      combinedDeductionDescriptions,
      contributionDescriptions,
      amortizationDescriptions,
      isOandoConsolidatedReport,
    ]
  )

  // AppTable drops the row pin and row number on a phone, and the summary row is
  // laid out by position, so its spacers for them have to go too.
  const isPhone = useMediaQuery(APP_TABLE_PHONE_QUERY)

  const summary = useMemo(
    () =>
      tableData.length
        ? () => renderSummaryFromMatrix(summaryMatrix, isPhone)
        : undefined,
    [summaryMatrix, tableData.length, isPhone]
  )

  return (
    <EnhancedReportTable
      title={title ? () => <>{title}</> : undefined}
      dataSource={tableData}
      columns={columns}
      rowKey="key"
      defaultFreezeBoundaryKey={variant === "detailed" ? "full_name" : "label"}
      pagination={false}
      scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
      tableLayout="auto"
      bordered
      summary={summary}
      exportFooter={summaryMatrix}
      locale={{
        emptyText: `There is no activity to show for ${data?.range}`,
      }}
    />
  )
}

const buildAmountGroup = (
  title: string,
  key: string,
  descriptions: string[],
  valueKey:
    | "earningValues"
    | "deductionValues"
    | "contributionValues"
    | "amortizationValues",
  user: any,
  totalKey?:
    | "totalEarningsLegacy"
    | "totalDeductions"
    | "totalContributions"
    | "totalAmortizations"
) => {
  const leafChildren = descriptions.length
    ? descriptions.map((description) => ({
        title: getPayrollJournalColumnTitle(key, description),
        key: `${key}_${description}`,
        render: (_value: any, record: any) =>
          money(
            user,
            record.totals[valueKey]?.[description] || 0,
            record.totals.currency
          ),
      }))
    : [
        {
          title: "Amount",
          key: `${key}_amount`,
          render: (_value: any, record: any) =>
            money(
              user,
              totalKey
                ? record.totals[totalKey]
                : Object.values(record.totals[valueKey] || {}).reduce(
                    (total: number, value: any) => total + Number(value || 0),
                    0
                  ),
              record.totals.currency
            ),
        },
      ]

  if (totalKey && descriptions.length) {
    leafChildren.push({
      title: "Total",
      key: `${key}_total`,
      render: (_value: any, record: any) =>
        money(user, record.totals[totalKey], record.totals.currency),
    })
  }

  return { title, key, children: leafChildren }
}

const getPayrollJournalColumnTitle = (
  sectionKey: string,
  description: string
) => {
  const isPension = getRetroBaseDescription(description) === "pension"
  const isRetro = isRetroDescription(description)

  if (isPension && sectionKey === "deductions") {
    return isRetro ? "Employee Pension Retro" : "Employee Pension"
  }

  if (isPension && sectionKey === "contributions") {
    return isRetro ? "Employer Pension Retro" : "Employer Pension"
  }

  return description
}

const buildOandoEarningsGroup = (descriptions: string[], user: any) => ({
  title: "Earnings",
  key: "earnings",
  children: descriptions.length
    ? [
        ...descriptions.map((description) => ({
          title: description,
          key: `earnings_${description}`,
          render: (_value: any, record: any) =>
            money(
              user,
              record.totals.earningValues?.[description] || 0,
              record.totals.currency
            ),
        })),
        {
          title: "Total",
          key: "earnings_total",
          render: (_value: any, record: any) =>
            money(user, record.totals.totalEarnings, record.totals.currency),
        },
      ]
    : [
        {
          title: "Amount",
          key: "earnings_amount",
          render: (_value: any, record: any) =>
            money(user, record.totals.totalEarnings, record.totals.currency),
        },
        {
          title: "Tax",
          key: "earnings_tax",
          render: (_value: any, record: any) =>
            money(user, record.totals.totalTax, record.totals.currency),
        },
      ],
})

const buildOandoDeductionsGroup = (descriptions: string[], user: any) => ({
  title: "Deductions",
  key: "deductions",
  children: descriptions.length
    ? [
        ...descriptions.map((description) => ({
          title: getPayrollJournalColumnTitle("deductions", description),
          key: `deductions_${description}`,
          render: (_value: any, record: any) =>
            money(
              user,
              record.totals.deductionValues?.[description] || 0,
              record.totals.currency
            ),
        })),
        {
          title: "Total",
          key: "deductions_total",
          render: (_value: any, record: any) =>
            money(user, record.totals.totalDeductions, record.totals.currency),
        },
      ]
    : [
        {
          title: "Amount",
          key: "deductions_amount",
          render: (_value: any, record: any) =>
            money(user, record.totals.totalDeductions, record.totals.currency),
        },
      ],
})

const buildEmployeeDeductionsGroup = (
  deductionDescriptions: string[],
  amortizationDescriptions: string[],
  data: any,
  user: any
) => {
  const children: any[] = [
    ...deductionDescriptions.map((description) => ({
      title: getPayrollJournalColumnTitle("deductions", description),
      key: `deductions_${description}`,
      render: (_value: any, record: any) =>
        money(
          user,
          record.totals.deductionValues?.[description] || 0,
          record.totals.currency
        ),
    })),
  ]

  if (data?.employeeTaxes) {
    children.push({
      title: "Tax",
      key: "deductions_tax",
      render: (_value: any, record: any) =>
        money(user, record.totals.totalTax, record.totals.currency),
    })
  }

  children.push(
    ...amortizationDescriptions.map((description) => ({
      title: getPayrollJournalColumnTitle("amortizations", description),
      key: `amortizations_${description}`,
      render: (_value: any, record: any) =>
        money(
          user,
          record.totals.amortizationValues?.[description] || 0,
          record.totals.currency
        ),
    }))
  )

  return {
    title: "Employee Deductions",
    key: "deductions",
    children: children.length
      ? children
      : [
          {
            title: "Amount",
            key: "deductions_amount",
            render: (_value: any, record: any) =>
              money(
                user,
                record.totals.totalDeductions,
                record.totals.currency
              ),
          },
        ],
  }
}

const getDescriptions = (
  items: any[] = [],
  predicate: (item: any) => boolean,
  mapper?: (item: any) => string
) =>
  Array.from(
    new Set(
      items
        .filter((item) => predicate(item) && item?.description)
        .map((item) => (mapper ? mapper(item) : item.description))
    )
  )

const getDeductionDescription = (item: any): string => {
  const description = item?.description || ""
  if (
    isAdvanceDeductionItem(item) &&
    !itemDescriptionIncludes(item, "advance") &&
    !description.startsWith("Tax ")
  ) {
    return `Tax ${description}`
  }
  return description
}

const getDeductionDescriptions = (items: any[] = []) =>
  Array.from(
    new Set(
      items.flatMap((item) => {
        if (!isDeductionItem(item)) return []

        const description = getDeductionDescription(item)
        return description ? [description] : []
      })
    )
  )

const sortDescriptionsWithRetrosNextToBase = (descriptions: string[] = []) => {
  const ordered: string[] = []
  const used = new Set<string>()
  const consolidatedSalary = descriptions.find(isConsolidatedSalary)
  const regularDescriptions = descriptions
    .filter(
      (description) =>
        !isConsolidatedSalary(description) && !isRetroDescription(description)
    )
    .sort((a, b) => a.localeCompare(b))
  const retroDescriptions = descriptions
    .filter(isRetroDescription)
    .sort((a, b) => a.localeCompare(b))

  const pushDescriptionWithRetros = (description: string) => {
    if (used.has(description)) return

    ordered.push(description)
    used.add(description)

    retroDescriptions.forEach((retroDescription) => {
      if (
        !used.has(retroDescription) &&
        getRetroBaseDescription(retroDescription) ===
          getRetroBaseDescription(description)
      ) {
        ordered.push(retroDescription)
        used.add(retroDescription)
      }
    })
  }

  if (consolidatedSalary) {
    pushDescriptionWithRetros(consolidatedSalary)
  }

  regularDescriptions.forEach(pushDescriptionWithRetros)
  retroDescriptions.forEach(pushDescriptionWithRetros)

  return ordered
}

const sortConsolidatedSalaryFirst = (descriptions: string[] = []) =>
  [...descriptions].sort((a, b) => {
    if (isConsolidatedSalary(a)) return -1
    if (isConsolidatedSalary(b)) return 1
    return a.localeCompare(b)
  })

const sortLegacyDeductionDescriptions = (descriptions: string[] = []) => {
  const taxColumns = descriptions.filter((description) =>
    description.toLowerCase().includes("tax")
  )
  const recoveryColumns = descriptions
    .filter((description) => description.toLowerCase().includes("recovery"))
    .sort((a, b) => a.localeCompare(b))
  const otherColumns = descriptions
    .filter(
      (description) =>
        !description.toLowerCase().includes("tax") &&
        !description.toLowerCase().includes("recovery")
    )
    .sort((a, b) => a.localeCompare(b))

  return [...otherColumns, ...recoveryColumns, ...taxColumns]
}

const sortDeductionDescriptions = (descriptions: string[] = []) => {
  const taxColumns = descriptions
    .filter(isTaxDescription)
    .sort(compareOandoTaxRowLabels)
  const recoveryColumns = descriptions.filter((description) =>
    description.toLowerCase().includes("recovery")
  )
  const pensionColumns = orderKnownDescriptions(descriptions, ["pension"])
  const voluntaryColumns = sortDescriptionsWithRetrosNextToBase(
    descriptions.filter(
      (description) =>
        isEmployeeVoluntaryContributionDescription(description) &&
        !pensionColumns.includes(description)
    )
  )
  const nhfColumns = orderKnownDescriptions(descriptions, ["nhf"])
  const statutoryColumns = [
    ...pensionColumns,
    ...voluntaryColumns,
    ...nhfColumns,
  ]
  const associationColumns = sortDescriptionsWithRetrosNextToBase(
    descriptions.filter(isAssociationDescription)
  )
  const otherColumns = descriptions.filter(
    (description) =>
      !isTaxDescription(description) &&
      !description.toLowerCase().includes("recovery") &&
      !statutoryColumns.includes(description) &&
      !isAssociationDescription(description)
  )
  const orderedRecoveryColumns =
    sortDescriptionsWithRetrosNextToBase(recoveryColumns)
  const orderedOtherColumns = sortDescriptionsWithRetrosNextToBase(otherColumns)

  return [
    ...statutoryColumns,
    ...associationColumns,
    ...orderedOtherColumns,
    ...orderedRecoveryColumns,
    ...taxColumns,
  ]
}

const isConsolidatedSalaryRetroTax = (description: string) => {
  const lower = description.toLowerCase()
  return lower.includes("consolidated salary") && lower.includes("retro")
}

const compareOandoTaxRowLabels = (a: string, b: string) => {
  const aFirst = isConsolidatedSalaryRetroTax(a)
  const bFirst = isConsolidatedSalaryRetroTax(b)
  if (aFirst && !bFirst) return -1
  if (bFirst && !aFirst) return 1
  return a.localeCompare(b)
}

const sortContributionDescriptions = (descriptions: string[] = []) => {
  const statutoryColumns = orderKnownDescriptions(descriptions, [
    "pension",
    "gratuity",
  ])
  const otherColumns = descriptions.filter(
    (description) => !statutoryColumns.includes(description)
  )

  return [
    ...statutoryColumns,
    ...sortDescriptionsWithRetrosNextToBase(otherColumns),
  ]
}

const getOandoTaxDescriptions = (
  items: any[] = [],
  isOandoConsolidatedReport: boolean
) => {
  if (!isOandoConsolidatedReport) return []

  const descriptions: string[] = []

  if (items.some((item) => itemDescriptionIncludes(item, "retro"))) {
    descriptions.push(TaxColumns.TAX_RETRO)
  }

  descriptions.push(TaxColumns.TOTAL_ALLOWANCE_TAX)

  if (items.some((item) => itemDescriptionIncludes(item, "differential"))) {
    descriptions.push(TaxColumns.TAX_DIFFERENTIAL)
  }

  descriptions.push(TaxColumns.TAX)

  return descriptions
}

const getRowId = (row: any, rowType: Props["rowType"]) => {
  if (rowType === "Employee") return row?.person_id
  if (rowType === "Department") return row?.department
  return row?.office_address_id || row?.office_display_name
}

const getRowLabel = (row: any, rowType: Props["rowType"]) => {
  if (rowType === "Employee") return row?.full_name
  if (rowType === "Department") return row?.department
  return row?.office_display_name
}

const getRowMeta = (row: any, rowType: Props["rowType"]) => {
  if (rowType === "Employee") return row?.identification_number
  if (rowType === "Workplace") return row?.office_address_phone
  return ""
}

const buildCompactIdentityColumn = (rowType: Props["rowType"]) => ({
  title: `${rowType} Information`,
  dataIndex: "label",
  key: "label",
  width: 240,
  render: (_value: any, record: any) => (
    <div className="flex flex-col">
      <span className="font-medium">{record.label || "--"}</span>
      {record.meta ? (
        <span className="text-xs text-neutral500">{record.meta}</span>
      ) : null}
    </div>
  ),
})

const buildDetailedIdentityColumns = (rowType: Props["rowType"]) => {
  if (rowType !== "Employee") return [buildCompactIdentityColumn(rowType)]

  return [
    {
      title: "Emp No.",
      key: "identification_number",
      width: 150,
      render: (_value: any, record: any) =>
        record.identity.identification_number || "--",
    },
    {
      title: "Full name",
      key: "full_name",
      width: 220,
      render: (_value: any, record: any) => record.identity.full_name || "--",
    },
    {
      title: "Department",
      key: "department",
      width: 180,
      render: (_value: any, record: any) => record.identity.department || "--",
    },
    {
      title: "Workplace",
      key: "office_display_name",
      width: 180,
      render: (_value: any, record: any) =>
        record.identity.office_display_name || "--",
    },
    {
      title: "Paygrade",
      key: "paygrade",
      width: 160,
      render: (_value: any, record: any) => record.identity.paygrade || "--",
    },
    {
      title: "Hire date",
      key: "hire_date",
      width: 140,
      render: (_value: any, record: any) =>
        formatReportDate(record.identity.hire_date) || "--",
    },
    {
      title: "RSA PIN",
      key: "rsa_pin",
      width: 160,
      render: (_value: any, record: any) => record.identity.rsa_pin || "--",
    },
    {
      title: "Gratuity PIN",
      key: "gratuity_pin",
      width: 160,
      render: (_value: any, record: any) =>
        record.identity.gratuity_pin || "--",
    },
    {
      title: "Date of birth",
      key: "dob",
      width: 140,
      render: (_value: any, record: any) =>
        formatReportDate(record.identity.dob) || "--",
    },
    {
      title: "NHF number",
      key: "nhf_epn",
      width: 160,
      render: (_value: any, record: any) => record.identity.nhf_epn || "--",
    },
    {
      title: "NHF location",
      key: "nhf_location",
      width: 160,
      render: (_value: any, record: any) =>
        record.identity.nhf_location || "--",
    },
    {
      title: "Tax office",
      key: "tax_office_name",
      width: 180,
      render: (_value: any, record: any) =>
        record.identity.tax_office_name || "--",
    },
    {
      title: "Tax number",
      key: "tin",
      width: 150,
      render: (_value: any, record: any) => record.identity.tin || "--",
    },
    {
      title: "HMO",
      key: "hmo",
      width: 180,
      render: (_value: any, record: any) => record.identity.hmo || "--",
    },
    {
      title: "PFA",
      key: "pfa_name",
      width: 180,
      render: (_value: any, record: any) => record.identity.pfa_name || "--",
    },
    {
      title: "Job title",
      key: "employee_title",
      width: 180,
      render: (_value: any, record: any) =>
        record.identity.employee_title || "--",
    },
    {
      title: "Cooperative",
      key: "cooperative",
      width: 160,
      render: (_value: any, record: any) => record.identity.cooperative || "--",
    },
    {
      title: "Union membership",
      key: "union_membership",
      width: 180,
      render: (_value: any, record: any) =>
        record.identity.union_membership || "--",
    },
    {
      title: "Assignment category",
      key: "assignment_category",
      width: 180,
      render: (_value: any, record: any) =>
        record.identity.assignment_category || "--",
    },
  ]
}

const buildDetailedIdentityGroup = (rowType: Props["rowType"]) => ({
  title: "Employee Information",
  key: "employee_information",
  children: buildDetailedIdentityColumns(rowType),
})

const shouldShowDaysWorkedColumn = (data: any, rowType: Props["rowType"]) =>
  rowType === "Employee" && Boolean(data?.daysWorked)

const buildDaysWorkedColumn = () => ({
  title: "Days worked",
  key: "days_worked",
  width: 130,
  render: (_value: any, record: any) => record.daysWorked || "--",
})

const getRowIdentity = (row: any, rowType: Props["rowType"]) => {
  if (rowType !== "Employee") return row || {}

  return {
    identification_number: row?.identification_number,
    full_name: row?.full_name,
    department: row?.department,
    office_display_name: row?.office_display_name,
    paygrade: row?.paygrade,
    hire_date: row?.hire_date,
    rsa_pin: row?.rsa_pin,
    gratuity_pin: row?.gratuity_pin,
    dob: row?.dob,
    nhf_epn: row?.nhf_epn,
    nhf_location: row?.nhf_location,
    tax_office_name: row?.tax_office_name,
    tin: row?.tin,
    hmo: row?.hmo_name || row?.hmo,
    pfa_name: row?.pfa_name,
    employee_title: row?.employee_title,
    cooperative: row?.cooperative,
    union_membership: row?.union_membership,
    assignment_category: row?.assignment_category,
  }
}

const getDaysWorkedLabel = (row: any) => {
  const daysWorked = row?.days_worked ?? row?.groups?.[0]?.days_worked
  const totalDays = row?.total_days ?? row?.groups?.[0]?.total_days

  if (daysWorked === null || daysWorked === undefined) return ""
  if (totalDays === null || totalDays === undefined) return String(daysWorked)

  return `${daysWorked}/${totalDays}`
}

const money = (user: any, value: number, fallbackSymbol?: string) =>
  `${fallbackSymbol || user?.currencySymbol || ""}${formatCurrencyNoSymbol(
    value
  )}`

const isEarningItem = (item: any) => {
  const typeId = Number(item?.payroll_item_type_id)
  if (typeId === 8) return Number(item?.amount || 0) > 0

  return EARNING_TYPES.includes(typeId) && Number(item?.amount || 0) > 0
}

const isRegularEarningItem = (item: any) =>
  isEarningItem(item) && !isReimbursementItem(item)

const isGrossEarningItem = (item: any) => {
  const typeId = Number(item?.payroll_item_type_id)
  if (typeId === 8) return isEarningItem(item)

  return GROSS_EARNING_TYPES.includes(typeId) && isEarningItem(item)
}

const isOtherPayableItem = (item: any) =>
  OTHER_PAYABLE_TYPES.includes(Number(item?.payroll_item_type_id)) &&
  isEarningItem(item)

const isAdvanceDeductionItem = (item: any) =>
  Number(item?.payroll_item_type_id) === 8 && !Number(item?.amount || 0)

const isDeductionItem = (item: any) =>
  Number(item?.payroll_item_type_id) === 2 || isAdvanceDeductionItem(item)

const isContributionItem = (item: any) =>
  Number(item?.payroll_item_type_id) === 5

const isAmortizationItem = (item: any) =>
  Number(item?.payroll_item_type_id) === 6

const isReimbursementItem = (item: any) =>
  Number(item?.payroll_item_type_id) === REIMBURSEMENT_TYPE

const getDeductionAmountForItem = (item: any) => {
  if (isAdvanceDeductionItem(item)) {
    if (itemDescriptionIncludes(item, "advance")) {
      return Number(item?.after_tax_amount || 0)
    }
    return Number(item?.tax_amount || 0)
  }
  return Number(
    item?.deducted_amount || item?.after_tax_amount || item?.amount || 0
  )
}

const getPositiveNumber = (value: unknown) => {
  const amount = Number(value || 0)
  return amount > 0 ? amount : 0
}

const getTotalEmployeeDeductions = (totals: any) =>
  Number(totals?.totalDeductions || 0) + Number(totals?.totalAmortizations || 0)

const isLegacyPayrollEarningItem = (item: any) =>
  LEGACY_PAYROLL_EARNING_TYPES.includes(Number(item?.payroll_item_type_id))

const isLegacyPayrollDeductionItem = (item: any) =>
  Number(item?.payroll_item_type_id) === 2

const getStatutoryBenefitDescriptions = (data: any) =>
  new Set<string>(
    (data?.statutoryBenefits || [])
      .map((benefit: any) => benefit?.description)
      .filter((description: unknown): description is string =>
        Boolean(description)
      )
  )

const isEmployerCostAddBackDeduction = (
  item: any,
  statutoryBenefitDescriptions: Set<string>
) =>
  (isLegacyPayrollDeductionItem(item) &&
    statutoryBenefitDescriptions.has(item?.description)) ||
  item?.description === "Health Service Provider"

const isEmployerCostContribution = (
  item: any,
  statutoryBenefitDescriptions: Set<string>
) =>
  (isContributionItem(item) &&
    statutoryBenefitDescriptions.has(item?.description)) ||
  itemDescriptionIncludes(item, "earned wage withdrawal")

const getLegacyJournalNetPay = (items: any[] = []) => {
  const personIds = Array.from(
    new Set(items.map((item) => item?.person_id).filter(Boolean))
  )
  const groupedItems =
    personIds.length > 0
      ? personIds.map((personId) =>
          items.filter((item) => item?.person_id === personId)
        )
      : [items]

  return groupedItems.reduce((total, personItems) => {
    const afterTaxEarnings = personItems
      .filter(isLegacyPayrollEarningItem)
      .reduce((sum, item) => sum + getPositiveNumber(item?.after_tax_amount), 0)
    const deductions = personItems
      .filter(isLegacyPayrollDeductionItem)
      .reduce((sum, item) => sum + getPositiveNumber(item?.deducted_amount), 0)

    return total + Math.max(afterTaxEarnings - deductions, 0)
  }, 0)
}

const getLegacyJournalEmployerCost = (items: any[] = [], data: any) => {
  const statutoryBenefitDescriptions = getStatutoryBenefitDescriptions(data)
  const earnings = items
    .filter(isLegacyPayrollEarningItem)
    .reduce((sum, item) => sum + getPositiveNumber(item?.amount), 0)
  const deductions = items
    .filter(isLegacyPayrollDeductionItem)
    .reduce((sum, item) => sum + getPositiveNumber(item?.deducted_amount), 0)
  const statutoryDeductionAddBack = items
    .filter((item) =>
      isEmployerCostAddBackDeduction(item, statutoryBenefitDescriptions)
    )
    .reduce((sum, item) => sum + getPositiveNumber(item?.deducted_amount), 0)
  const employerContributions = items
    .filter((item) =>
      isEmployerCostContribution(item, statutoryBenefitDescriptions)
    )
    .reduce((sum, item) => sum + getPositiveNumber(item?.amount), 0)

  return (
    earnings - deductions + statutoryDeductionAddBack + employerContributions
  )
}

const getRowTotals = (items: any[] = [], data: any) => {
  const earningValues: Record<string, number> = {}
  const earningTaxValues: Record<string, number> = {}
  const deductionValues: Record<string, number> = {}
  const contributionValues: Record<string, number> = {}
  const amortizationValues: Record<string, number> = {}
  let totalEarnings = 0
  let totalEarningsLegacy = 0
  let totalDeductions = 0
  let advanceTaxAmount = 0
  let totalContributions = 0
  let totalAmortizations = 0
  let totalTax = 0

  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const description = item?.description

    if (isEarningItem(item)) {
      const amount = Number(item?.amount || 0)
      totalEarnings += amount
      if (isLegacyPayrollEarningItem(item)) {
        totalEarningsLegacy += amount
      }
      if (description) {
        earningValues[description] = (earningValues[description] || 0) + amount
        earningTaxValues[description] =
          (earningTaxValues[description] || 0) + Number(item?.tax_amount || 0)
      }
    }

    if (item?.payroll_item_type_id == 8) {
      advanceTaxAmount += item?.tax_amount
    }

    if (isDeductionItem(item)) {
      const amount = getDeductionAmountForItem(item)
      totalDeductions += amount
      const deductionDescription = getDeductionDescription(item)
      if (deductionDescription) {
        deductionValues[deductionDescription] =
          (deductionValues[deductionDescription] || 0) + amount
      }
    }

    if (isContributionItem(item)) {
      const amount = Number(item?.amount || 0)
      totalContributions += amount
      if (description) {
        contributionValues[description] =
          (contributionValues[description] || 0) + amount
      }
    }

    if (isAmortizationItem(item)) {
      const amount = Number(item?.amount || 0)
      totalAmortizations += amount
      if (description) {
        amortizationValues[description] =
          (amortizationValues[description] || 0) + amount
      }
    }

    if (isEarningType(item) || isAdvanceDeductionItem(item)) {
      totalTax += Number(item?.tax_amount || 0)
    }
  }

  const hasConsolidatedSalary = items.some(
    (item) =>
      isConsolidatedSalary(item?.description) ||
      getRetroBaseDescription(item?.description) === "consolidated salary"
  )

  if (hasConsolidatedSalary) {
    deductionValues[TaxColumns.CONSOLIDATED_TAX] = sumTaxByDescription(
      items,
      (item) => isConsolidatedSalary(item?.description)
    )
    deductionValues[TaxColumns.TOTAL_ALLOWANCE_TAX] = sumTaxByDescription(
      items,
      (item) =>
        isEarningType(item) &&
        !isConsolidatedSalary(item?.description) &&
        !itemDescriptionIncludes(item, "retro") &&
        !itemDescriptionIncludes(item, "differential")
    )
    deductionValues[TaxColumns.TAX_RETRO] = sumTaxByDescription(items, (item) =>
      itemDescriptionIncludes(item, "retro")
    )
    deductionValues[TaxColumns.TAX_DIFFERENTIAL] = sumTaxByDescription(
      items,
      (item) => itemDescriptionIncludes(item, "differential")
    )
    deductionValues[TaxColumns.TAX] = totalTax
  }

  totalDeductions += totalTax - advanceTaxAmount

  return {
    earningValues,
    earningTaxValues,
    deductionValues,
    contributionValues,
    amortizationValues,
    totalEarnings,
    totalEarningsLegacy,
    totalDeductions,
    totalContributions,
    totalAmortizations,
    totalTax,
    netPay: getLegacyJournalNetPay(items),
    employerCost: getLegacyJournalEmployerCost(items, data),
    currency: items?.[0]?.currency_symbol,
  }
}

const isEarningType = (item: any) =>
  EARNING_TYPES.includes(Number(item?.payroll_item_type_id))

const isConsolidatedSalary = (description?: string | null) =>
  (description || "").toLowerCase() === "consolidated salary"

const isRetroDescription = (description?: string | null) =>
  (description || "").toLowerCase().includes("retro")

const getRetroBaseDescription = (description?: string | null) =>
  (description || "")
    .toLowerCase()
    .replace(/\bretro\b/g, "")
    .replace(/\s+/g, " ")
    .trim()

const normalizeDescription = (description?: string | null) =>
  (description || "").toLowerCase().replace(/\s+/g, " ").trim()

const orderKnownDescriptions = (
  descriptions: string[],
  knownOrder: string[]
) => {
  const selected: string[] = []

  knownOrder.forEach((knownDescription) => {
    const matchingDescriptions = descriptions.filter((description) => {
      const normalized = normalizeDescription(description)
      return (
        normalized === knownDescription ||
        getRetroBaseDescription(description) === knownDescription
      )
    })
    selected.push(...sortDescriptionsWithRetrosNextToBase(matchingDescriptions))
  })

  return Array.from(new Set(selected))
}

const isTaxDescription = (description?: string | null) =>
  normalizeDescription(description).includes("tax")

const isAssociationDescription = (description?: string | null) =>
  normalizeDescription(description).includes("association")

const isEmployeeVoluntaryContributionDescription = (
  description?: string | null
) => {
  const normalizedDescription = normalizeDescription(description)
  return (
    normalizedDescription.includes("voluntary") ||
    normalizedDescription.includes("evc")
  )
}

const itemDescriptionIncludes = (item: any, searchText: string) =>
  (item?.description || "").toLowerCase().includes(searchText)

const sumTaxByDescription = (
  items: any[] = [],
  predicate: (item: any) => boolean
) =>
  items
    .filter(
      (item) =>
        isEarningType(item) && !isAdvanceDeductionItem(item) && predicate(item)
    )
    .map((item) => Number(item?.tax_amount || 0))
    .reduce((total, value) => total + value, 0)

type SummaryMatrixArgs = {
  allItems: any[]
  user: any
  data: any
  identityLeafCount: number
  earningDescriptions: string[]
  grossEarningDescriptions: string[]
  otherPayableDescriptions: string[]
  combinedDeductionDescriptions: string[]
  contributionDescriptions: string[]
  amortizationDescriptions: string[]
  isOandoConsolidatedReport: boolean
}

const buildSummaryMatrix = ({
  allItems,
  user,
  data,
  identityLeafCount,
  earningDescriptions,
  grossEarningDescriptions,
  otherPayableDescriptions,
  combinedDeductionDescriptions,
  contributionDescriptions,
  amortizationDescriptions,
  isOandoConsolidatedReport,
}: SummaryMatrixArgs): ExportFooterCell[][] => {
  const itemsByCurrency = new Map<string, any[]>()
  for (const item of allItems) {
    const currency = getItemCurrencySymbol(item, user)
    if (!itemsByCurrency.has(currency)) {
      itemsByCurrency.set(currency, [])
    }
    itemsByCurrency.get(currency)!.push(item)
  }

  if (!itemsByCurrency.size) return []

  const currencies = Array.from(itemsByCurrency.keys())

  return currencies.map((currency, currencyIndex) => {
    const items = itemsByCurrency.get(currency) || []
    const row: ExportFooterCell[] = []

    for (let i = 0; i < identityLeafCount; i++) {
      row.push({
        value:
          i === 0 ? (
            <span className="font-semibold">
              {currencies.length > 1 && currency
                ? `Totals (${currency})`
                : "Totals"}
            </span>
          ) : (
            ""
          ),
      })
    }

    const pushAmount = (amount: number) => {
      row.push({ value: money(user, amount, currency) })
    }

    const includeInEarningsSummary = (item: any) => isEarningItem(item)

    const sumEarningDescription = (desc: string) =>
      items
        .filter((g) => includeInEarningsSummary(g) && g?.description === desc)
        .reduce((acc, g) => acc + Number(g?.amount || 0), 0)

    const sumDeductionDescription = (desc: string) =>
      items
        .filter(
          (g) => isDeductionItem(g) && getDeductionDescription(g) === desc
        )
        .reduce((acc, g) => acc + getDeductionAmountForItem(g), 0)

    const sumAmortizationDescription = (desc: string) =>
      items
        .filter((g) => isAmortizationItem(g) && g?.description === desc)
        .reduce((acc, g) => acc + Number(g?.amount || 0), 0)

    const legacyEarningsTotal = items
      .filter(isLegacyPayrollEarningItem)
      .reduce((acc, g) => acc + Number(g?.amount || 0), 0)

    const totalsForCurrency = getRowTotals(items, data)

    if (data?.employeeEarnings) {
      if (isOandoConsolidatedReport && earningDescriptions.length) {
        for (const desc of earningDescriptions) {
          pushAmount(sumEarningDescription(desc))
        }
        pushAmount(legacyEarningsTotal)
      } else if (!isOandoConsolidatedReport) {
        if (grossEarningDescriptions.length) {
          for (const desc of grossEarningDescriptions) {
            pushAmount(sumEarningDescription(desc))
          }
        }

        for (const desc of otherPayableDescriptions) {
          pushAmount(sumEarningDescription(desc))
        }
      } else {
        pushAmount(
          items
            .filter(includeInEarningsSummary)
            .reduce((acc, g) => acc + Number(g?.amount || 0), 0)
        )
        if (isOandoConsolidatedReport) {
          pushAmount(
            items
              .filter(includeInEarningsSummary)
              .reduce((acc, g) => acc + Number(g?.tax_amount || 0), 0)
          )
        } else {
          pushAmount(legacyEarningsTotal)
        }
      }
    } else {
      pushAmount(
        items
          .filter(includeInEarningsSummary)
          .reduce((acc, g) => acc + Number(g?.amount || 0), 0)
      )
    }

    const totalTaxAllItems = items
      .filter((g) => isEarningType(g) && !isAdvanceDeductionItem(g))
      .reduce((acc, g) => acc + Number(g?.tax_amount || 0), 0)

    const pushDeductionSummary = () => {
      if (!isOandoConsolidatedReport) {
        if (!data?.deductions) {
          pushAmount(getTotalEmployeeDeductions(totalsForCurrency))
          pushAmount(totalTaxAllItems)
          return
        }

        for (const desc of combinedDeductionDescriptions) {
          pushAmount(sumDeductionDescription(desc))
        }
        if (data?.employeeTaxes) {
          pushAmount(totalTaxAllItems)
        }
        for (const desc of amortizationDescriptions) {
          pushAmount(sumAmortizationDescription(desc))
        }
        if (
          !combinedDeductionDescriptions.length &&
          !data?.employeeTaxes &&
          !amortizationDescriptions.length
        ) {
          pushAmount(getTotalEmployeeDeductions(totalsForCurrency))
        }
        return
      }

      if (data?.deductions) {
        if (combinedDeductionDescriptions.length) {
          for (const desc of combinedDeductionDescriptions) {
            let amount = 0
            if (desc === TaxColumns.CONSOLIDATED_TAX) {
              amount = sumTaxByDescription(items, (item) =>
                isConsolidatedSalary(item?.description)
              )
            } else if (desc === TaxColumns.TOTAL_ALLOWANCE_TAX) {
              amount = sumTaxByDescription(
                items,
                (item) =>
                  !isConsolidatedSalary(item?.description) &&
                  !itemDescriptionIncludes(item, "retro") &&
                  !itemDescriptionIncludes(item, "differential")
              )
            } else if (desc === TaxColumns.TAX_RETRO) {
              amount = sumTaxByDescription(items, (item) =>
                itemDescriptionIncludes(item, "retro")
              )
            } else if (desc === TaxColumns.TAX_DIFFERENTIAL) {
              amount = sumTaxByDescription(items, (item) =>
                itemDescriptionIncludes(item, "differential")
              )
            } else if (desc === TaxColumns.TAX) {
              amount = totalTaxAllItems
            } else {
              amount = sumDeductionDescription(desc)
            }
            pushAmount(amount)
          }
          if (isOandoConsolidatedReport) {
            pushAmount(
              items
                .filter(isDeductionItem)
                .reduce((acc, g) => acc + getDeductionAmountForItem(g), 0) +
                totalTaxAllItems
            )
          }
        } else {
          pushAmount(
            items
              .filter(isDeductionItem)
              .reduce((acc, g) => acc + getDeductionAmountForItem(g), 0) +
              totalTaxAllItems
          )
        }
      } else {
        pushAmount(
          items
            .filter(isDeductionItem)
            .reduce((acc, g) => acc + getDeductionAmountForItem(g), 0) +
            totalTaxAllItems
        )
        pushAmount(totalTaxAllItems)
      }
    }

    if (!isOandoConsolidatedReport) {
      pushDeductionSummary()
    }

    if (data?.contributions) {
      if (contributionDescriptions.length) {
        for (const desc of contributionDescriptions) {
          pushAmount(
            items
              .filter((g) => isContributionItem(g) && g?.description === desc)
              .reduce((acc, g) => acc + Number(g?.amount || 0), 0)
          )
        }
      } else {
        pushAmount(
          items
            .filter(isContributionItem)
            .reduce((acc, g) => acc + Number(g?.amount || 0), 0)
        )
      }
      pushAmount(
        items
          .filter(isContributionItem)
          .reduce((acc, g) => acc + Number(g?.amount || 0), 0)
      )
    }

    if (isOandoConsolidatedReport && amortizationDescriptions.length) {
      for (const desc of amortizationDescriptions) {
        pushAmount(sumAmortizationDescription(desc))
      }
      pushAmount(
        items
          .filter(isAmortizationItem)
          .reduce((acc, g) => acc + Number(g?.amount || 0), 0)
      )
    }

    if (isOandoConsolidatedReport) {
      pushDeductionSummary()
    }

    pushAmount(getLegacyJournalNetPay(items))

    if (isOandoConsolidatedReport && data?.totals) {
      pushAmount(getLegacyJournalEmployerCost(items, data))
    }

    if (!isOandoConsolidatedReport) {
      pushAmount(legacyEarningsTotal)
      pushAmount(getTotalEmployeeDeductions(totalsForCurrency))
    }

    return row
  })
}

const getItemCurrencySymbol = (item: any, user: any) =>
  item?.currency_symbol || user?.currencySymbol || ""

const renderSummaryFromMatrix = (
  matrix: ExportFooterCell[][],
  isPhone: boolean
): ReactNode => {
  if (!matrix.length) return null

  return (
    <Table.Summary fixed>
      {matrix.map((row, rowIndex) => (
        <Table.Summary.Row key={`summary-row-${rowIndex}`}>
          {isPhone ? null : (
            <>
              <Table.Summary.Cell
                index={0}
                key={`summary-pin-spacer-${rowIndex}`}
              />
              <Table.Summary.Cell
                index={1}
                key={`summary-serial-spacer-${rowIndex}`}
              />
            </>
          )}
          {row.map((cell, cellIndex) => (
            <Table.Summary.Cell
              index={cellIndex + (isPhone ? 0 : 2)}
              key={`summary-cell-${rowIndex}-${cellIndex}`}
            >
              {cell.value}
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      ))}
    </Table.Summary>
  )
}
